import { useEffect, useRef, useState } from "react";
import { useLocalization } from "@fluent/react";
import {
  Button,
  ContentCard,
  Drawer,
  Row,
  Tag,
  TruncatedAccount,
} from "@narmi/design_system";
import { catcat as cc } from "cerulean";
import Filters from "byzantine/src/filters";
import { useACHCompany } from "byzantine/src/dbbl/businessLogic/entities/achCompanies";
import type { PaginatedDualApproval } from "byzantine/src/dbbl/businessLogic/entities/dualApprovals/types";
import type { FormattedBulkTemplate } from "byzantine/src/dbbl/businessLogic/entities/bulkTemplates/types";
import { formatPaymentTotal } from "../../PaymentActivity/CreateBulk/templates/templateHelpers";
import activityStyles from "../../PaymentActivity/PaymentActivity.module.scss";
import ViewPaymentsDialog from "../../Templates/BulkTemplates/Drawer/ViewPaymentsDialog";
import DualApprovalRejectApproveDialog from "../common/DualApprovalRejectApproveDialog";
import styles from "../DualApproval.module.scss";

type TemplateDualApprovalDrawerProps = {
  isOpen: boolean;
  showControls: boolean;
  handleClose: () => void;
  handleOnNext: () => void;
  handleOnPrev: () => void;
  handleApprove?: () => void;
  handleReject?: (note?: string) => void;
  isActionLoading?: boolean;
  dualApproval: PaginatedDualApproval | null;
  drawerType?: "pending" | "history";
  titleL10nKey?: string;
};

const TemplateDualApprovalDrawer = ({
  isOpen,
  showControls,
  handleClose,
  handleOnNext,
  handleOnPrev,
  handleApprove,
  handleReject,
  isActionLoading = false,
  dualApproval,
  drawerType = "pending",
  titleL10nKey = "template-review-drawer-title",
}: TemplateDualApprovalDrawerProps) => {
  const { l10n } = useLocalization();
  const drawerContentRef = useRef<HTMLDivElement>(null);
  const achCompany = useACHCompany();
  const [isPaymentsDialogOpen, setIsPaymentsDialogOpen] = useState(false);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);

  useEffect(() => {
    if (drawerContentRef.current) {
      drawerContentRef.current.scrollIntoView({
        behavior: "instant",
      });
    }
  }, [isOpen, dualApproval?.id]);

  if (!dualApproval || !isOpen) return null;

  const { templateVersion } = dualApproval;
  if (!templateVersion) return null;

  const showActions = Boolean(
    drawerType === "pending" &&
      !dualApproval.isOwnRequest &&
      handleApprove &&
      handleReject
  );
  const rejectionReason =
    dualApproval.status === "rejected"
      ? dualApproval.rejectionReason?.trim()
      : "";

  const {
    templateName,
    versionNumber,
    institutionAccountName,
    institutionAccountLastFour,
    secCode,
    entryDesc,
    description,
    maxExpediteChoice,
    groupPosting,
    paymentCount,
    paymentCreditCount,
    paymentDebitCount,
    creditTotalDollars,
    debitTotalDollars,
    userChangeLog,
  } = templateVersion;

  const renderDetailRow = (label: string, value: React.ReactNode) => (
    <div className="margin--bottom--l">
      <Row alignItems="center">
        <Row.Item shrink>
          <span className="fontColor--secondary">{label}</span>
        </Row.Item>
        <Row.Item>
          <div className={activityStyles.rowContent}>
            <span className={activityStyles.ellipsisWrapperRightAligned}>
              {value}
            </span>
          </div>
        </Row.Item>
      </Row>
    </div>
  );

  return (
    <Drawer
      depth="524px"
      isOpen={isOpen}
      showControls={showControls}
      onUserDismiss={handleClose}
      paddingSize="xl"
      onNext={handleOnNext}
      onPrev={handleOnPrev}
      {...(showActions && {
        footer: (
          <Row alignItems="center" justifyContent="space-between">
            <Row.Item shrink>
              <Button
                kind="negative"
                onClick={() => setIsRejectDialogOpen(true)}
                label={l10n.getString("approval-action-reject")}
                testId="reject-template-approval"
                disabled={isActionLoading}
              />
            </Row.Item>
            <Row.Item shrink>
              <Button
                kind="primary"
                onClick={handleApprove}
                label={l10n.getString("approval-action-approve")}
                testId="approve-template-approval"
                disabled={isActionLoading}
              />
            </Row.Item>
          </Row>
        ),
      })}
    >
      <div ref={drawerContentRef}>
        <Row alignItems="center">
          <Row.Item shrink>
            <h1 className="padding--top--l">{l10n.getString(titleL10nKey)}</h1>
          </Row.Item>
          <Row.Item>
            <div className={cc("padding--top--l", styles.drawerStatusTag)}>
              {drawerType === "history" ? (
                <Tag
                  kind={
                    dualApproval.status === "rejected" ||
                    dualApproval.status === "expired"
                      ? "error"
                      : "success"
                  }
                  label={Filters.humanize(dualApproval.status)}
                />
              ) : null}
            </div>
          </Row.Item>
        </Row>

        <div className="margin--top--l">
          <ContentCard kind="bordered">
            <h5 className={styles.drawerLabelHeading}>
              {l10n.getString("label-template-name", {}, "Template name")}
            </h5>
            <div className="margin--bottom--m">{templateName}</div>

            {rejectionReason ? (
              <div className="margin--bottom--m">
                <h5 className={styles.drawerLabelHeading}>
                  {l10n.getString("template-reject-reason-label")}
                </h5>
                <div>{rejectionReason}</div>
              </div>
            ) : null}

            {paymentCreditCount > 0 && creditTotalDollars ? (
              <>
                <h5 className={styles.drawerLabelHeading}>
                  {l10n.getString("label-sending")}
                </h5>
                <div className="margin--bottom--m">
                  {formatPaymentTotal(
                    l10n,
                    creditTotalDollars,
                    paymentCreditCount
                  )}
                </div>
              </>
            ) : null}

            {paymentDebitCount > 0 && debitTotalDollars ? (
              <>
                <h5 className={styles.drawerLabelHeading}>
                  {l10n.getString("label-collecting")}
                </h5>
                <div className="margin--bottom--m">
                  {formatPaymentTotal(
                    l10n,
                    debitTotalDollars,
                    paymentDebitCount
                  )}
                </div>
              </>
            ) : null}

            {paymentCount > 0 ? (
              <>
                <h5 className={cc("margin--none", styles.drawerLabelHeading)}>
                  {l10n.getString(
                    "label-template-details",
                    {},
                    "Template details"
                  )}
                </h5>
                <Button
                  kind="plain"
                  size="s"
                  onClick={() => setIsPaymentsDialogOpen(!isPaymentsDialogOpen)}
                >
                  {l10n.getString("view-payments-cta", {
                    total: paymentCount,
                  })}
                  <span className="narmi-icon-chevron-right" />
                </Button>
              </>
            ) : null}

            {groupPosting ? (
              <div className="fontSize--xs fontColor--primary margin--top--s">
                {l10n.getString("group-posting-copy")}
              </div>
            ) : null}
          </ContentCard>
        </div>

        {userChangeLog ? (
          <div className="margin--top--xl">
            <h5 className="margin--bottom--xs fontColor--secondary fontWeight--bold">
              {l10n
                .getString("edit-details-heading", {}, "Edit details")
                .toUpperCase()}
            </h5>
            <ContentCard kind="bordered">
              <div className="margin--bottom--xs">
                <h5 className={styles.drawerLabelHeading}>
                  {l10n.getString("change-log-label", {}, "Change log")}
                </h5>
                <div className={styles.changeLogContainer}>{userChangeLog}</div>
              </div>
            </ContentCard>
          </div>
        ) : null}

        <div className="margin--top--xl">
          <h5 className="margin--bottom--xs fontColor--secondary fontWeight--bold">
            {l10n
              .getString("label-template-details", {}, "Template details")
              .toUpperCase()}
          </h5>
          <ContentCard kind="bordered">
            {renderDetailRow(
              l10n.getString("label-version"),
              `V${versionNumber}`
            )}

            {institutionAccountName || institutionAccountLastFour
              ? renderDetailRow(
                  l10n.getString("label-originating-account"),
                  <TruncatedAccount
                    name={institutionAccountName ?? ""}
                    lastFour={institutionAccountLastFour ?? ""}
                  />
                )
              : null}

            {!(institutionAccountName || institutionAccountLastFour) &&
            dualApproval.fromAccountDisplay
              ? renderDetailRow(
                  l10n.getString("label-originating-account"),
                  dualApproval.fromAccountDisplay
                )
              : null}

            {renderDetailRow(
              l10n.getString("speed-label"),
              maxExpediteChoice > 0
                ? l10n.getString("label-allowed-to-expedite")
                : l10n.getString("label-not-allowed-to-expedite")
            )}

            {secCode
              ? renderDetailRow(
                  l10n.getString("label-payment-type", {}, "Payment type"),
                  secCode
                )
              : null}

            {entryDesc
              ? renderDetailRow(
                  l10n.getString(
                    "label-purpose-payment",
                    {},
                    "Purpose of payment"
                  ),
                  entryDesc
                )
              : null}

            {description
              ? renderDetailRow(
                  l10n.getString("label-note-internal"),
                  description
                )
              : null}

            {achCompany?.company_id
              ? renderDetailRow(
                  l10n.getString("label-company-name-id"),
                  `${achCompany.company_name}/${achCompany.company_id}`
                )
              : null}

            {renderDetailRow(
              l10n.getString("label-submitted-by"),
              dualApproval.requesterName
            )}

            {renderDetailRow(
              l10n.getString("label-submitted-on"),
              Filters.longMonthDayYear(dualApproval.createdAt)
            )}
          </ContentCard>
        </div>
      </div>

      <ViewPaymentsDialog
        isOpen={isPaymentsDialogOpen}
        handleClose={() => setIsPaymentsDialogOpen(false)}
        template={
          { paymentData: templateVersion.paymentData } as FormattedBulkTemplate
        }
        variant="template"
      />
      <DualApprovalRejectApproveDialog
        isOpen={isRejectDialogOpen}
        type="reject"
        handleClose={() => setIsRejectDialogOpen(false)}
        handleReject={(note) => handleReject?.(note)}
        handleApprove={() => undefined}
        totalSelected={1}
        variant="template"
        hasNoActiveVersion={!templateVersion.templateGroupHasActiveVersion}
      />
    </Drawer>
  );
};

export default TemplateDualApprovalDrawer;
